import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * shadcn-style Button bound to our brand tokens. Use this when you need
 * `asChild` (Radix Slot composition) or the destructive / outline variants
 * the brand-kit Button doesn't ship. For everyday primary CTAs the existing
 * `<Button />` in components/ui/ is still the brand-anchored default.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium outline-none transition select-none disabled:pointer-events-none disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-offset-0',
  {
    variants: {
      variant: {
        default:
          'bg-brand text-brand-contrast shadow-[inset_0_1px_0_rgb(255_255_255/0.12)] hover:opacity-90 focus-visible:ring-brand/40',
        destructive:
          'bg-danger text-fg-inverse hover:opacity-90 focus-visible:ring-danger/40',
        outline:
          'border border-border bg-bg text-fg hover:bg-bg-subtle focus-visible:ring-brand/30',
        secondary:
          'bg-bg-subtle text-fg border border-border hover:bg-bg-muted focus-visible:ring-brand/30',
        ghost:
          'bg-transparent text-fg hover:bg-bg-subtle focus-visible:ring-brand/30',
        link: 'text-brand underline-offset-4 hover:underline focus-visible:underline',
      },
      size: {
        default: 'h-10 px-4',
        sm: 'h-8 px-3',
        lg: 'h-11 px-5 text-base',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        type={asChild ? undefined : type ?? 'button'}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
